import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { junkRunLine } from '../../utils/themeAIJunk'
import { personalDataRunLine } from '../../utils/themeAIPersonalData'
import DownloadIcon from '@mui/icons-material/Download'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StageSection from './StageSection'
import DisabledReason from '../system/DisabledReason'
import RunProgress from './RunProgress'
import { themeColourFor } from '../../utils/themeAIThemeColours'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ResultsFilterPanel, { ThemeAILoopedFilter } from './ResultsFilterPanel'
import ResponseList from './ResponseList'
import WriteVariableDialog from './WriteVariableDialog'
import type { ThemeAIRecordsView } from './TestRunStage'
import { buildThemeDistribution } from '../../utils/themeAIDistribution'
import { writeVariableDialogReducer, WriteVariableDialogStep } from '../../utils/themeAIWriteback'
import type { ThemeAIRun, ThemeAIRunResultsResponse, ThemeAITheme } from '../../types/gotypes'
import { THEMEAI_FILE_UPLOAD } from '../../utils/themeAIFeatures'

export type { ThemeAILoopedFilter }

export interface ThemeAIWriteBackView {
  /** Only job-variable tasks can write back (upload write-back is Wave 2). */
  available: boolean
  enabled: boolean
  /** Shown in the dialog when coded responses predate the current frame. */
  warning?: string
  defaultVariableName: string
  writing: boolean
  onWrite: (variableName: string, overwrite: boolean) => Promise<ThemeAIWriteOutcome>
  /** Opens the written variable's table in Tables, or Tables itself when no table was made. */
  onOpenTable: (written: ThemeAIWrittenVariable) => void
}

/** Where a write-back landed (#1484): the variable, and the starter table made for it. */
export interface ThemeAIWrittenVariable {
  variable: string
  folder: string
  customer: string
  job: string
  /** Tables/User/<email>/themeai/<variable>; absent when the table could not be made. */
  table?: string
  tableError?: string
  /** Sentiment and emotion, written beside the themes variable when the run coded them (#1594). */
  companions?: ThemeAIWrittenCompanion[]
}

/** A sentiment or emotion variable written beside the themes one (#1594). */
export interface ThemeAIWrittenCompanion {
  kind: 'sentiment' | 'emotion'
  variable: string
  table?: string
  tableError?: string
  /** Why it was not written; the themes variable stands either way. */
  error?: string
}

export type ThemeAIWriteOutcome = { status: 'conflict' } | ({ status: 'written' } & ThemeAIWrittenVariable)

// Long enough to register that something happened, even when the write is
// instant (Matt, 21 Sep: "it might have done it instantly, but … slow it down").
const MIN_WRITE_FEEDBACK_MS = 1200
// What each result action gives you (Matt, 24 Sep: a tooltip on each).
const DOWNLOAD_DATA_HINT = 'Download all respondent-level data as a CSV file: every response in this run with the codes it was given.'
const DOWNLOAD_TABLE_HINT = 'Download the results table as an Excel file: how many responses each theme has, with sentiment and emotion when they were coded.'
const WRITE_VARIABLE_HINT = 'Save every response coded so far as a new multi-response variable in this job, with a themes table in My Tables > themeai. When sentiment and emotion were coded, they are saved too, each as its own variable and table.'

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

interface ResultsStageProps {
  runs: ThemeAIRun[]
  activeRun?: ThemeAIRun
  /** The active run was coded before the data or code frame last changed. */
  activeRunStale: boolean
  onSelectRun: (runNumber: number) => void
  results?: ThemeAIRunResultsResponse
  resultsLoading: boolean
  resultsError?: string
  frameThemes: ThemeAITheme[]
  records: ThemeAIRecordsView
  selectedThemeCode?: string
  onSelectTheme: (code: string | undefined) => void
  /** A sentiment or emotion code filtering the responses, instead of a theme. */
  selectedLooped?: ThemeAILoopedFilter
  onSelectLooped: (filter: ThemeAILoopedFilter | undefined) => void
  onDownloadCsv: (runNumber: number) => void
  /** Downloads the results table as Excel (themes, and sentiment/emotion when coded). */
  onDownloadExcel: () => void
  uploadingCorrections: boolean
  onUploadCorrections: (file: File) => Promise<void>
  writeBack: ThemeAIWriteBackView
  onGoToRun: () => void
  /** Why this person cannot change the task (upload corrections, write the variable), or undefined. Viewing and downloading stay open. */
  writeBlockedReason?: string
}

const statusChip = (run: ThemeAIRun) => {
  const color = run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'info'
  return <Chip size="small" variant="outlined" color={color} label={run.status} />
}

const formatDate = (ms: number) =>
  new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

// Results lead with the answer — how the responses split across the themes,
// and the responses behind each share — for the latest run on the current
// data. What to do with them sits in the header; the audit trail of every run
// is one click down.
const ResultsStage: React.FC<ResultsStageProps> = ({
  runs,
  activeRun,
  activeRunStale,
  onSelectRun,
  results,
  resultsLoading,
  resultsError,
  frameThemes,
  records,
  selectedThemeCode,
  onSelectTheme,
  selectedLooped,
  onSelectLooped,
  onDownloadCsv,
  onDownloadExcel,
  uploadingCorrections,
  onUploadCorrections,
  writeBack,
  onGoToRun,
  writeBlockedReason,
}) => {
  const [historyOpen, setHistoryOpen] = useState(false)
  const [writeOpen, setWriteOpen] = useState(false)
  const [writeStep, setWriteStep] = useState<WriteVariableDialogStep>('form')
  const [variableName, setVariableName] = useState('')
  const [holding, setHolding] = useState(false)
  const [written, setWritten] = useState<ThemeAIWrittenVariable>()

  const distribution = results ? buildThemeDistribution(results.themes, activeRunStale ? undefined : frameThemes) : []
  const selectedTheme = distribution.find((item) => item.code === selectedThemeCode)
  const themeColour = themeColourFor(activeRunStale ? [] : frameThemes)
  const sentimentItems = results?.sentiment ? buildThemeDistribution(results.sentiment, undefined) : []
  const emotionItems = results?.emotion ? buildThemeDistribution(results.emotion, undefined) : []
  const selectedLoopedItem = selectedLooped
    ? (selectedLooped.kind === 'sentiment' ? sentimentItems : emotionItems).find((item) => item.code === selectedLooped.code)
    : undefined
  const filterLabel = selectedLooped
    ? `${selectedLooped.kind === 'sentiment' ? 'Sentiment' : 'Emotion'}: ${selectedLoopedItem?.name ?? selectedLooped.code}`
    : selectedTheme?.name

  const handleCorrectionsFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      await onUploadCorrections(file)
    } catch {
      // The hook reports the server's error.
    }
  }

  const openWriteDialog = () => {
    setVariableName(writeBack.defaultVariableName)
    setWriteStep('form')
    setWriteOpen(true)
  }

  const submitWrite = async (overwrite: boolean) => {
    setHolding(true)
    try {
      const [outcome] = await Promise.all([writeBack.onWrite(variableName.trim(), overwrite), wait(MIN_WRITE_FEEDBACK_MS)])
      if (outcome.status === 'conflict') {
        setWriteStep((step) => writeVariableDialogReducer(step, { type: 'CONFLICT' }))
        return
      }
      const { status: _status, ...landed } = outcome
      setWritten(landed)
      setWriteOpen(false)
      setWriteStep('form')
    } catch {
      // The hook reports the error; the dialog stays open to retry.
    } finally {
      setHolding(false)
    }
  }

  const writeButton = (
    <Button
      variant="contained"
      color="info"
      startIcon={<SaveAltIcon />}
      disabled={!writeBack.enabled || writeBack.writing || holding}
      onClick={openWriteDialog}
    >
      {writeBack.writing || holding ? 'Writing to the job…' : 'Write to job variable'}
    </Button>
  )

  const handleWriteConfirm = () => {
    if (writeStep === 'confirm1') {
      setWriteStep(writeVariableDialogReducer(writeStep, { type: 'CONFIRM' }))
      return
    }
    if (writeStep === 'confirm2') submitWrite(true)
  }

  if (!activeRun) {
    return (
      <StageSection title="Results" description="Nothing has been coded yet.">
        <Box>
          <Button variant="contained" color="info" onClick={onGoToRun}>Set up the full run</Button>
        </Box>
      </StageSection>
    )
  }

  const completed = activeRun.status === 'completed'
  const runLine = [
    `Run #${activeRun.run_number}${activeRun.is_test ? ' (test)' : ''}`,
    `${activeRun.response_count.toLocaleString()} responses`,
    formatDate(activeRun.completed_at || activeRun.created_at),
    // Responses the model provider's content filter blocked, or that the
    // model could not code; each says why in the list (#1596).
    ...(activeRun.records_failed ? [`${activeRun.records_failed.toLocaleString()} could not be coded`] : []),
    // What the run did with the answers a junk check flagged (#1891).
    ...(junkRunLine(activeRun.junk_decision, activeRun.junk_count) ? [junkRunLine(activeRun.junk_decision, activeRun.junk_count) as string] : []),
    // And how many it flagged as containing personal data, code 98 (#494).
    ...(personalDataRunLine(activeRun.personal_data_decision, activeRun.personal_data_count)
      ? [personalDataRunLine(activeRun.personal_data_decision, activeRun.personal_data_count) as string] : []),
  ].join(' · ')

  // The page header says "Results"; what used to be a second title and a
  // caption above the chart is one info box (Matt, 24 Sep, #1523 item 4).
  const summary = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.5,
        py: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: '75ch',
      }}
    >
      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: '2px' }} />
      <Typography variant="body2" color="text.secondary">
        {runLine}.
        {completed && results && ` Percentages are of all ${results.total_records.toLocaleString()} responses, and a response can carry several themes.`}
      </Typography>
    </Box>
  )

  return (
    <StageSection
      summary={summary}
      actions={
        <>
          {completed && !activeRun.is_test && (
            <Button color="info" onClick={onGoToRun}>Run again</Button>
          )}
          <Tooltip describeChild title={DOWNLOAD_DATA_HINT}>
            <span>
              <Button
                variant="outlined"
                color="info"
                startIcon={<DownloadIcon />}
                disabled={activeRun.status === 'pending'}
                onClick={() => onDownloadCsv(activeRun.run_number)}
              >
                Download Data
              </Button>
            </span>
          </Tooltip>
          {completed && results && (
            <Tooltip describeChild title={DOWNLOAD_TABLE_HINT}>
              <Button variant="outlined" color="info" startIcon={<DownloadIcon />} onClick={onDownloadExcel}>
                Download Table
              </Button>
            </Tooltip>
          )}
          {writeBack.available && (writeBlockedReason ? (
            <DisabledReason reason={writeBlockedReason}>{writeButton}</DisabledReason>
          ) : (
            <Tooltip describeChild title={WRITE_VARIABLE_HINT}>
              <span>{writeButton}</span>
            </Tooltip>
          ))}
        </>
      }
    >
      {written && (
        <Alert
          severity={written.table && (written.companions ?? []).every((c) => c.table) ? 'success' : 'warning'}
          onClose={() => setWritten(undefined)}
          action={
            <Button color="info" size="small" onClick={() => writeBack.onOpenTable(written)}>
              {written.table ? 'See the table' : 'Open Tables'}
            </Button>
          }
        >
          Written to “{written.variable}”.{' '}
          {written.table
            ? 'Its table, themes by count, is in My Tables > themeai.'
            : `Its table could not be made${written.tableError ? ` (${written.tableError})` : ''}. The variable is ready to use in Tables.`}
          {(written.companions ?? []).map((c) => (
            <Box key={c.kind} data-testid={`written-${c.kind}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              {c.error ? (
                <span>{c.kind === 'sentiment' ? 'Sentiment' : 'Emotion'} was not written: {c.error}</span>
              ) : (
                <>
                  <span>
                    {c.kind === 'sentiment' ? 'Sentiment' : 'Emotion'} written to “{c.variable}”
                    {c.table ? '.' : `; its table could not be made${c.tableError ? ` (${c.tableError})` : ''}.`}
                  </span>
                  {c.table && (
                    <Button
                      color="info"
                      size="small"
                      onClick={() => writeBack.onOpenTable({ ...written, variable: c.variable, table: c.table, tableError: c.tableError })}
                    >
                      See the {c.kind} table
                    </Button>
                  )}
                </>
              )}
            </Box>
          ))}
        </Alert>
      )}
      {activeRunStale && (
        <Alert severity="info">
          This run was coded before the data or code frame last changed. Its results are kept for reference.
        </Alert>
      )}

      {activeRun.status === 'pending' || activeRun.status === 'running' ? (
        <Box>
          <RunProgress run={activeRun} label={`Coding ${activeRun.response_count.toLocaleString()} responses…`} />
          {activeRun.status === 'pending' && (
            <Button color="info" sx={{ mt: 1.5, ml: -1 }} onClick={onGoToRun}>This run has not started — go to Run</Button>
          )}
        </Box>
      ) : activeRun.status === 'failed' ? (
        <Alert severity="error" action={<Button color="inherit" onClick={onGoToRun}>Go to Run</Button>}>
          Run #{activeRun.run_number} failed{activeRun.error ? `: ${activeRun.error}` : '.'}
        </Alert>
      ) : resultsLoading ? (
        <CircularProgress size={24} />
      ) : resultsError || !results ? (
        <Alert severity="error">The results for run #{activeRun.run_number} could not be loaded.</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 5fr) 7fr' },
            gap: 4,
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              position: { lg: 'sticky' },
              top: { lg: 16 },
              display: 'flex',
              maxHeight: { lg: 'calc(100vh - 64px - 32px)' },
            }}
          >
            <ResultsFilterPanel
              themes={distribution}
              sentiment={sentimentItems}
              emotion={emotionItems}
              totalResponses={results.total_records}
              themeColour={themeColour}
              selectedThemeCode={selectedThemeCode}
              selectedLooped={selectedLooped}
              onSelectTheme={onSelectTheme}
              onSelectLooped={onSelectLooped}
            />
          </Box>
          <ResponseList
            records={records.records}
            total={records.total}
            offset={records.offset}
            pageSize={records.pageSize}
            loading={records.loading}
            selectedCode={selectedThemeCode}
            selectedThemeName={filterLabel}
            onSelectTheme={onSelectTheme}
            onClearFilter={() => (selectedLooped ? onSelectLooped(undefined) : onSelectTheme(undefined))}
            filtered={Boolean(selectedLooped) || selectedThemeCode !== undefined}
            themeColour={themeColour}
            onPageChange={records.onPageChange}
          />
        </Box>
      )}

      {THEMEAI_FILE_UPLOAD && completed && !activeRun.is_test && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Found a code to change? Edit the downloaded CSV and upload it back.
          </Typography>
          <input type="file" accept=".csv" id="theme-ai-corrections-upload" style={{ display: 'none' }} onChange={handleCorrectionsFile} disabled={Boolean(writeBlockedReason)} />
          <label htmlFor="theme-ai-corrections-upload">
            <DisabledReason reason={writeBlockedReason}>
              <Button component="span" size="small" color="info" startIcon={<UploadFileIcon />} disabled={uploadingCorrections}>
                {uploadingCorrections ? 'Uploading…' : 'Upload corrections'}
              </Button>
            </DisabledReason>
          </label>
        </Box>
      )}

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
        <Button
          color="info"
          onClick={() => setHistoryOpen((open) => !open)}
          aria-expanded={historyOpen}
          endIcon={<ExpandMoreIcon sx={{ transform: historyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease-out' }} />}
          sx={{ ml: -1 }}
        >
          Run history ({runs.length})
        </Button>
        <Collapse in={historyOpen}>
          <Table size="small" aria-label="Theme AI run history" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Run</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell>Junk</TableCell>
                <TableCell>When</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow
                  key={run.id}
                  hover
                  selected={run.run_number === activeRun.run_number}
                  onClick={() => onSelectRun(run.run_number)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>#{run.run_number}</TableCell>
                  <TableCell>{run.is_test ? 'Test' : 'Full run'}</TableCell>
                  <TableCell>{statusChip(run)}</TableCell>
                  <TableCell>{run.response_scope === 'new_only' ? 'New only' : 'All'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{run.response_count.toLocaleString()}</TableCell>
                  <TableCell>{junkRunLine(run.junk_decision, run.junk_count) ?? '—'}</TableCell>
                  <TableCell>{formatDate(run.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip describeChild title={DOWNLOAD_DATA_HINT}>
                      <span>
                        <Button
                          size="small"
                          color="info"
                          startIcon={<DownloadIcon />}
                          disabled={run.status === 'pending'}
                          onClick={(event) => {
                            event.stopPropagation()
                            onDownloadCsv(run.run_number)
                          }}
                        >
                          Data
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </Box>

      <WriteVariableDialog
        open={writeOpen}
        step={writeStep}
        variableName={variableName}
        onVariableNameChange={setVariableName}
        loading={writeBack.writing || holding}
        summary={
          writeBack.warning
            ? `This publishes every response coded so far for this task as a new multi-response variable in the job. ${writeBack.warning}`
            : 'This publishes every response coded so far for this task as a new multi-response variable in the job.'
        }
        onSubmit={() => submitWrite(false)}
        onConfirm={handleWriteConfirm}
        onCancel={() => !(writeBack.writing || holding) && setWriteOpen(false)}
      />
    </StageSection>
  )
}

export default ResultsStage
